import {match} from 'ts-pattern';

import {isNil} from 'ramda';

/**
 * Resolves a raw enum `const_key` (e.g. `MAKE_PORSCHE`, `TRANSMISSION_MANUAL`)
 * to a human-readable label using the provided lookup map.
 *
 * Falls back to the original value when the key is not found in the map,
 * so pre-formatted strings from dev/test fixtures pass through unchanged.
 */
export function resolveAuctionEnumLabel(
  constKey: string | null | undefined,
  lookupMap: ReadonlyMap<string, string>
): string | null {
  if (isNil(constKey)) {
    return null;
  }

  const directLabel = lookupMap.get(constKey);
  if (!isNil(directLabel)) {
    return directLabel;
  }

  // Data from Alpha can contain style const keys from an intersecting vehicle type
  // (e.g. VANSTYLE_* in passenger-car context). Try intersecting style prefixes.
  const vehicleType = deriveVehicleTypeFromCarStyle(constKey);
  const styleSuffix = constKey.split('_').slice(1).join('_');
  const stylePrefixes = match<string | null, string[]>(vehicleType)
    .with('VEHICLETYPE_PASSENGER_CAR', () => ['CARSTYLE', 'VANSTYLE'])
    .with('VEHICLETYPE_VAN', () => ['VANSTYLE', 'CARSTYLE'])
    .otherwise(() => []);
  const fallbackKey = stylePrefixes
    .map((stylePrefix) => `${stylePrefix}_${styleSuffix}`)
    .find((styleKey) => !isNil(lookupMap.get(styleKey)));

  return isNil(fallbackKey) ? constKey : (lookupMap.get(fallbackKey) ?? constKey);
}

// Data sent by Alpha are sometimes inconsistent – body style has value
// for another vehicle type (e.g. VANSTYLE_* in passenger-car context).
function deriveVehicleTypeFromCarStyle(carStyle: string) {
  const carStyleBase = carStyle.split('_')[0];
  const vehicleTypeBase = carStyleBase.substring(0, carStyleBase.length - 'STYLE'.length);

  return match<string, string>(vehicleTypeBase)
    .with('CAR', () => 'VEHICLETYPE_PASSENGER_CAR')
    .otherwise(() => `VEHICLETYPE_${vehicleTypeBase}`);
}
